import waitForTimers from 'wait-for-timers';
import { useShallow } from 'zustand/shallow';
import { usePodcastsStore } from '../../global-state/podcasts';
import { useSessionStore } from '../../global-state/session';
import {
  userInteractedWithEpisodeIds,
  useUserDataStore,
} from '../../global-state/user-data';
import { Episode, PodcastId } from '../../models/podcasts';
import { useBumpEpisodePriorityWhenInView } from '../../utils/bump-episode-priority-when-in-view';
import { checkIfPlayed } from '../../utils/check-if-played';
import { EpisodeItem } from '../episode-item';

const LatestEpisodes = ({ podcastIds }: { podcastIds: PodcastId[] }) => {
  const podcasts = usePodcastsStore((s) => s.podcasts);
  const dismissedRecentEpisodeIds = useSessionStore((s) => s.dismissedRecentEpisodeIds);
  const dismissRecentEpisode = useSessionStore((s) => s.dismissRecentEpisode);

  const maxPerPodcast = 15;

  const episodesOfEachPodcast: Episode[][] = podcastIds
    .map((id) => podcasts[id])
    .filter(Boolean)
    .map((podcast) =>
      // Filter out omitted episodes that the user hasn't interacted with
      podcast.episodes.filter(
        (ep) => !ep.omit || userInteractedWithEpisodeIds.has(ep.id)
      )
    );

  // Once one podcast runs out of recent episodes, stop showing further episodes
  // b/c the order is no longer known. Worse case scenario, we only show 20 episodes
  // of a single recent podcast b/c no other podcast has any episodes more recent
  // than its 20th episode
  const { pubDate: dateCutoff } =
    episodesOfEachPodcast
      .map((episodes) => episodes[maxPerPodcast - 1])
      .filter(Boolean)
      .sort((a, b) => (a.pubDate > b.pubDate ? -1 : 1))[0] || {};

  // Get episodes from store instead of query to ensure they have latest metadata
  const sortedEpisodes = episodesOfEachPodcast
    .map((episodes) => episodes.slice(0, maxPerPodcast))
    .flat()
    .sort((a, b) => (a.pubDate > b.pubDate ? -1 : 1))
    .filter((episode) => !dateCutoff || episode.pubDate >= dateCutoff)
    .filter(
      (episode) =>
        !dismissedRecentEpisodeIds.includes(`${episode.podcastId}|${episode.guidHash}`)
    );

  const sortedUnplayedEpisodes = useUserDataStore(
    useShallow((s) => {
      const { episodesProgress } = s;
      return sortedEpisodes.filter(
        (episode) =>
          !checkIfPlayed(
            episode,
            episodesProgress[`${episode.podcastId}|${episode.guidHash}`]
          )
      );
    })
  );

  const bumpEpisodePriorityWhenInView = useBumpEpisodePriorityWhenInView();

  return (
    <div>
      {sortedUnplayedEpisodes.map((episode) => (
        <EpisodeItem
          key={episode.id}
          episode={episode}
          swipeAction={{
            onSwipe: (animationDuration: number) => {
              waitForTimers([animationDuration, 'ric', 'raf'], () =>
                dismissRecentEpisode(episode)
              );
              return 'dismiss';
            },
            text: 'Dismiss',
            color: 'gray',
          }}
          createContentRef={bumpEpisodePriorityWhenInView}
        ></EpisodeItem>
      ))}
    </div>
  );
};

export default LatestEpisodes;
